import React from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert,
    Grid, Typography, Chip, Divider, Box, Table, TableHead, TableBody, TableRow, TableCell,
} from "@mui/material";
import moment from "moment";

// Claim and authorization dates arrive as UTC midnight, so they are formatted
// in UTC -- reading them locally would show the previous day.
function formatUtcDate(value) {
    const ms = value ? Date.parse(value) : NaN;
    if (Number.isNaN(ms)) return "—";
    const date = new Date(ms);
    return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${String(date.getUTCFullYear()).slice(2)}`;
}

// Claim.SubmitDate is written as a real UTC instant (Claims.js), so unlike the
// service dates it is shown in local time.
function formatLocalDate(value) {
    return value ? moment(value).format("M/D/YY") : "—";
}

function formatCurrency(value) {
    return Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });
}

// Deterministic fact keys rendered as a "Label: value" grid — matches the
// shape helpers/complianceGaps.js returns for a claim or a prior-auth row.
const FACT_FIELDS = [
    ["claim_id", "Claim ID"],
    ["claim_status", "Claim Status"],
    ["prior_auth_id", "Prior Auth ID"],
    ["prior_auth", "Prior Auth #"],
    ["procedure_code", "Procedure Code"],
    ["procedure_modifier", "Procedure Modifier"],
    ["service_type", "Service Type"],
    ["service_start_date", "Service Start", formatUtcDate],
    ["service_end_date", "Service End", formatUtcDate],
    ["auth_start_date", "Auth Start", formatUtcDate],
    ["auth_end_date", "Auth End", formatUtcDate],
    ["units_billed", "Units Billed"],
    ["units_delivered", "Units Delivered"],
    ["units_approved", "Units Approved"],
    ["unit_of_service", "Unit of Service"],
    ["amount_billed", "Amount Billed", formatCurrency],
    ["submitted_at", "Submitted", formatLocalDate],
];

// Minutes of delivered time behind a task -- the evidence behind a units mismatch.
function deliveredLabel(task) {
    if (task.delivered_minutes === null || task.delivered_minutes === undefined) return "—";
    return `${task.delivered_minutes} min`;
}

// evaluated: false is a gap with no task narrative, so the model was never asked --
// different from "No Verdict", which is a model call that failed or could not be read.
function verdictLabel(verdict) {
    if (verdict?.evaluated === false) return "Not Evaluated";
    if (!verdict || verdict.justified === null || verdict.justified === undefined) return "No Verdict";
    return verdict.justified ? "Justified" : "Not Justified";
}

function verdictColor(verdict) {
    if (!verdict || verdict.justified === null || verdict.justified === undefined) return "default";
    return verdict.justified ? "success" : "error";
}

// A task with no compliance record comes back with justified: null -- that is
// "could not be checked", not "not justified".
// Labelled by what the rule checks (forms submitted, MT approved), not "Justified":
// it never reads what the forms say -- that is the Model Narrative Verdict's job.
function taskChip(task) {
    if (task.justified === null || task.justified === undefined) {
        return <Chip size="small" label="No Record" />;
    }
    const labels = task.justification_rule === "approved_mt"
        ? ["MT Approved", "MT Not Approved"]
        : ["Forms Submitted", "Forms Missing"];
    return (
        <Chip
            size="small"
            label={task.justified ? labels[0] : labels[1]}
            color={task.justified ? "success" : "error"}
        />
    );
}

export default function ComplianceDetails({ open, onClose, record }) {
    if (!record) return null;
    const facts = record.sql_facts || {};
    const verdict = record.llm_verdict || {};
    const tasks = facts.tasks || [];
    const claims = record.gap_type === "prior_auth" ? facts.claims || [] : [];

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {record.gap_type === "prior_auth" ? "Prior Authorization" : "Claim"} Gap
                {" — "}{record.customer_name || (record.cus_id ? `Customer #${record.cus_id}` : "No Matching Client")}
            </DialogTitle>
            <DialogContent dividers>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Facts
                </Typography>
                <Grid container spacing={1} sx={{ mb: 2 }}>
                    {FACT_FIELDS.filter(([key]) => facts[key] !== undefined && facts[key] !== null).map(([key, label, format]) => (
                        <Grid size={{ xs: 6, md: 4 }} key={key}>
                            <Typography variant="caption" color="text.secondary">{label}</Typography>
                            <Typography variant="body2">{format ? format(facts[key]) : String(facts[key])}</Typography>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ mb: 2 }}>
                    {(facts.issues || []).map((issue) => (
                        <Chip key={issue.code} label={issue.detail || issue.code} color="warning" size="small" sx={{ mr: 1, mb: 1 }} />
                    ))}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Model Narrative Verdict
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Chip
                        label={verdictLabel(verdict)}
                        color={verdictColor(verdict)}
                        variant={verdict.evaluated === false ? "outlined" : "filled"}
                    />
                    {verdict.confidence && <Chip label={`Confidence: ${verdict.confidence}`} variant="outlined" size="small" />}
                </Box>
                <Typography variant="body2" sx={{ mb: 2 }}>
                    {verdict.rationale || "No rationale returned."}
                    {verdict.evaluated === false && " The AI model only runs when there is task narrative to judge."}
                </Typography>
                {verdict.error && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        The AI evaluation failed ({verdict.error}). The next sweep will try again.
                    </Alert>
                )}
                {verdict.parse_error && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        The model's response could not be read as a verdict. Raw response:
                        <Box
                            component="pre"
                            sx={{ mt: 1, mb: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.75rem" }}
                        >
                            {verdict.parse_error}
                        </Box>
                    </Alert>
                )}

                {claims.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Linked Claims ({claims.length})
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Claim ID</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Units Billed</TableCell>
                                    <TableCell>Amount Billed</TableCell>
                                    <TableCell>Service Period</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {claims.map((claim) => (
                                    <TableRow key={claim.claim_id}>
                                        <TableCell>{claim.claim_id}</TableCell>
                                        <TableCell>{claim.claim_status || "—"}</TableCell>
                                        <TableCell>{claim.units_billed ?? "—"}</TableCell>
                                        <TableCell>
                                            {claim.amount_billed === null || claim.amount_billed === undefined ? "—" : formatCurrency(claim.amount_billed)}
                                        </TableCell>
                                        <TableCell>
                                            {formatUtcDate(claim.service_start_date)} – {formatUtcDate(claim.service_end_date)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}

                {tasks.length > 0 && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                            Linked Tasks ({tasks.length})
                        </Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Task ID</TableCell>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Service Date</TableCell>
                                    <TableCell>Performed By</TableCell>
                                    <TableCell>Delivered</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {tasks.map((task) => (
                                    <React.Fragment key={task.task_id}>
                                        <TableRow sx={{ "& > td": { borderBottom: task.justification_reason || task.missing_forms?.length ? "none" : undefined } }}>
                                            <TableCell>{task.task_id}</TableCell>
                                            <TableCell>{task.title || "—"}</TableCell>
                                            {/* tas_start_date is stored as wall-clock time and arrives
                                                as UTC, so it is read in UTC (as PreAuth.js does) --
                                                a local read would shift the visit by the UTC offset. */}
                                            <TableCell>{task.service_date ? moment.utc(task.service_date).format("M/D/YY h:mm A") : "—"}</TableCell>
                                            <TableCell>{task.performed_by || "—"}</TableCell>
                                            <TableCell>{deliveredLabel(task)}</TableCell>
                                            <TableCell>{taskChip(task)}</TableCell>
                                        </TableRow>
                                        {(task.justification_reason || task.missing_forms?.length > 0) && (
                                            <TableRow>
                                                <TableCell colSpan={6} sx={{ pt: 0 }}>
                                                    {task.justification_reason && (
                                                        <Typography variant="caption" color="text.secondary" component="div">
                                                            {task.justification_reason}
                                                        </Typography>
                                                    )}
                                                    {task.missing_forms?.length > 0 && (
                                                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                                                            <Typography variant="caption" color="text.secondary">Missing forms:</Typography>
                                                            {task.missing_forms.map((form) => (
                                                                <Chip key={form} label={form} size="small" variant="outlined" color="warning" />
                                                            ))}
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}
